/**
 * Search's loading shapes — one composite `pendingComponent` plus the two
 * per-field skeletons it, and the route's own `Suspense` fallbacks, render.
 *
 * `Label` renders for real throughout: it's static chrome, no data behind it.
 */
import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'
import { useSearchPageContext } from '@/features/search/ai/hooks/use-search-page-context'

import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Mirrors `IntegrationSelect` — the Log Source + Event Type field pair.
 */
export const IntegrationSelectSkeleton = () => {
	return (
		<>
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<Label>Log Source</Label>
				<Skeleton className="h-10 w-full" />
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<Label>Event Type</Label>
				<Skeleton className="h-10 w-full" />
			</div>
		</>
	)
}

/**
 * Mirrors `TimeRange` — the timeframe trigger + presets button.
 */
export const TimeRangeSkeleton = () => {
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-2">
			<Label>Timeframe</Label>

			<div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
				<Skeleton className="size-10 shrink-0" />
				<Skeleton className="h-10 min-w-0 flex-1" />
			</div>
		</div>
	)
}

/**
 * The route's `pendingComponent`. Composes the two field skeletons above with
 * bars for the query input, filters row, and sidebar/results split — the
 * page's other sections don't have their own named skeleton yet, so those
 * regions stay generic bars rather than gaining single-use exports here.
 *
 * `AIRailLayout` wraps this because it mirrors the real page's outermost
 * element: the rail is static chrome with no server dependency, so leaving it
 * out here would make it pop in and re-flow the content column once the route
 * resolves. `useSearchPageContext` reads URL search params only, so it resolves
 * during the pending state without suspending. The real page also passes a
 * `ref` to `<Section>` for container-width measurement — the skeleton has no
 * width-dependent child, so it deliberately doesn't.
 */
export const SearchSkeleton = () => {
	const pageContext = useSearchPageContext()

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section>
				<SectionTitle>Search</SectionTitle>
				<SectionDescription>
					Search, filter, and investigate log data across your connected
					integrations to quickly identify suspicious activity and security
					events.
				</SectionDescription>

				<div className="flex w-full flex-col gap-4">
					<div className="flex flex-col gap-4 sm:flex-row">
						<IntegrationSelectSkeleton />
						<TimeRangeSkeleton />
					</div>

					<Skeleton className="h-10 w-full" />
				</div>

				<div className="my-8 flex flex-row gap-6">
					<Skeleton className="h-full w-64" />
					<Skeleton className="h-full w-full" />
				</div>
			</Section>
		</AIRailLayout>
	)
}
